import * as React from 'react';
import { ScrollContainer } from 'react-router-scroll';
import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';

import Row from 'ha/ui/next/Row';
import Col from 'ha/ui/next/Col';
import units from 'ha/theme/units';
import { IntlProps } from 'ha/i18n';
import withNotifyScroll from 'ha/helpers/withNotifyScroll';
import { ListingPreviewCard } from 'ha/services/apiV2/types/AlgoliaListing';
import { CopyText } from '../types';
import SearchResultsBottom from './SearchResultsBottom';
import SearchResultsEmpty from './SearchResultsEmpty';
import ListingPreviewCardTracked from '../containers/ListingPreviewCardTracked';
import SearchUrgencyCard from '../components/SearchUrgencyCard';

interface LocationShape {
  query: {
    startDate?: string;
    endDate?: string;
  };
}

interface Props {
  location: LocationShape;
  items: ListingPreviewCard[];
  isAuthenticated: boolean;
  cityCanonical?: string;
  countryCanonical?: string;
  shouldShowCitiesNearby: boolean;
  onHighlight: (id: number) => void;
  onUnhighlight: (id: number) => void;
  onScroll: () => void;
  cityLocalizedName?: string;
  countryLocalizedName?: string;
  trackSearchResults: (items: ListingPreviewCard[]) => void;
  data?: {
    cityPagePathname?: string;
    countryPagePathname?: string;
    copyText?: CopyText;
  };
}

const styles = (t: Theme) =>
  createStyles({
    seoMargin: {
      margin: units.yx(0.75, 0),
    },
    scrollWrapper: {
      flexGrow: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: units.yx(0, 1.5),
      [t.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
  });

export class SearchResults extends React.Component<
  Props & IntlProps & WithStyles<typeof styles>
> {
  componentDidMount() {
    const { items, trackSearchResults, onScroll } = this.props;

    trackSearchResults(items);

    window.addEventListener('scroll', onScroll);
  }

  componentWillUnmount() {
    const { onScroll } = this.props;

    window.removeEventListener('scroll', onScroll);
  }

  render() {
    const {
      classes,
      items,
      location: {
        query: { startDate, endDate },
      },
      onHighlight,
      onUnhighlight,
      shouldShowCitiesNearby,
      onScroll,
      isAuthenticated,
      cityCanonical,
      countryCanonical,
      cityLocalizedName,
      countryLocalizedName = '',
      data = {},
    } = this.props;

    return (
      <ScrollContainer
        scrollKey="search-results"
        shouldUpdateScroll={() => [0, 0]}
      >
        <div
          className={classes.scrollWrapper}
          onScroll={isAuthenticated ? undefined : onScroll}
        >
          {items.length > 0 ? (
            <React.Fragment>
              {startDate && endDate && <SearchUrgencyCard />}
              <Row spacing={24}>
                {items.map((room, index) => (
                  <Col xs={12} sm={12} md={6} key={room.id}>
                    <ListingPreviewCardTracked
                      listingId={room.id}
                      listingProps={{
                        room,
                        key: room.id,
                        onMouseEnter: onHighlight,
                        onMouseLeave: onUnhighlight,
                        isLazy: index >= 4,
                        withGallery: true,
                        withFavorites: true,
                      }}
                      trackLocation="search"
                    />
                  </Col>
                ))}
              </Row>
            </React.Fragment>
          ) : (
            <SearchResultsEmpty />
          )}
          <SearchResultsBottom
            data={data}
            cityCanonical={cityCanonical}
            countryCanonical={countryCanonical}
            cityLocalizedName={cityLocalizedName}
            countryLocalizedName={countryLocalizedName}
            shouldShowCitiesNearby={shouldShowCitiesNearby}
          />
        </div>
      </ScrollContainer>
    );
  }
}

export default withNotifyScroll({
  debounce: 300,
})(withStyles(styles)(SearchResults));
